import React, { useState, useEffect, useRef, useCallback } from 'react';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';

const PREVIEW_DURATION = 10;

const MusicAdmin = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', artist: 'DayClap', audio_url: '', emoji: '🎵',
    category: 'both', is_premium: false, sort_order: 0
  });
  const [saving, setSaving] = useState(false);

  // Preview audio state
  const [previewTrackId, setPreviewTrackId] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setPreviewTrackId(null);
    setPreviewProgress(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, [stopPreview]);

  useEffect(() => { loadTracks(); }, []);

  const loadTracks = async () => {
    if (!supabase) return;
    try {
      const { data } = await supabase
        .from('music_tracks')
        .select('*')
        .order('sort_order', { ascending: true });
      if (data) setTracks(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!supabase || !form.title) return;
    setSaving(true);
    try {
      if (editing) {
        await supabase.from('music_tracks').update(form).eq('id', editing.id);
      } else {
        await supabase.from('music_tracks').insert(form);
      }
      await loadTracks();
      resetForm();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!supabase || !confirm('Delete this track?')) return;
    await supabase.from('music_tracks').update({ is_active: false }).eq('id', id);
    await loadTracks();
  };

  const handleToggleActive = async (track) => {
    if (!supabase) return;
    await supabase.from('music_tracks').update({ is_active: !track.is_active }).eq('id', track.id);
    await loadTracks();
  };

  const startEdit = (track) => {
    stopPreview();
    setEditing(track);
    setForm({
      title: track.title, artist: track.artist, audio_url: track.audio_url,
      emoji: track.emoji, category: track.category,
      is_premium: track.is_premium, sort_order: track.sort_order
    });
    setShowForm(true);
  };

  const resetForm = () => {
    stopPreview();
    setShowForm(false);
    setEditing(null);
    setForm({ title: '', artist: 'DayClap', audio_url: '', emoji: '🎵', category: 'both', is_premium: false, sort_order: 0 });
  };

  const togglePreview = (url, trackId) => {
    // If same track is playing, stop it
    if (previewTrackId === trackId) {
      stopPreview();
      return;
    }

    // Stop current preview
    stopPreview();

    if (!url) return;

    const audio = new Audio(url);
    audio.volume = 0.5;
    audioRef.current = audio;
    setPreviewTrackId(trackId);

    audio.play().then(() => {
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const pct = Math.min((elapsed / PREVIEW_DURATION) * 100, 100);
        setPreviewProgress(pct);
      }, 100);

      timeoutRef.current = setTimeout(() => {
        stopPreview();
      }, PREVIEW_DURATION * 1000);
    }).catch(() => {});

    audio.onended = () => stopPreview();
    audio.onerror = () => stopPreview();
  };

  // Preview for form audio URL
  const toggleFormPreview = () => {
    if (!form.audio_url) return;
    togglePreview(form.audio_url, 'form-preview');
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Music Library</h2>
          <p className="text-gray-500 mt-1">Manage music tracks for invitations</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold px-5 py-2.5 rounded-xl shadow-lg shadow-pink-200 flex items-center gap-2"
        >
          <SafeIcon name="Plus" /> Add Track
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-bold text-gray-800 mb-4">{editing ? 'Edit Track' : 'New Track'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
              <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none" placeholder="Track name" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Artist</label>
              <input type="text" value={form.artist} onChange={e => setForm({...form, artist: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Audio URL (MP3 link)</label>
              <div className="flex gap-2">
                <input type="url" value={form.audio_url} onChange={e => setForm({...form, audio_url: e.target.value})}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none" placeholder="https://example.com/track.mp3" />
                {form.audio_url && (
                  <button
                    onClick={toggleFormPreview}
                    className={`px-4 py-3 rounded-xl transition-colors flex items-center gap-2 ${
                      previewTrackId === 'form-preview'
                        ? 'bg-pink-500 text-white'
                        : 'bg-pink-50 text-pink-600 hover:bg-pink-100'
                    }`}
                  >
                    {previewTrackId === 'form-preview' ? (
                      <>
                        <div className="flex gap-0.5">
                          <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" />
                          <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                        </div>
                        <span className="text-xs font-medium">Stop</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon name="Play" />
                        <span className="text-xs font-medium">Preview</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              {previewTrackId === 'form-preview' && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                  <div className="bg-pink-500 h-full rounded-full transition-all duration-100" style={{ width: `${previewProgress}%` }} />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Emoji</label>
              <input type="text" value={form.emoji} onChange={e => setForm({...form, emoji: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none text-center text-2xl" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none">
                <option value="both">Both (Singles & Couples)</option>
                <option value="singles">Singles Only</option>
                <option value="couples">Couples Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 outline-none" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input type="checkbox" checked={form.is_premium} onChange={e => setForm({...form, is_premium: e.target.checked})}
                className="w-5 h-5 rounded border-gray-300 text-pink-500" />
              <span className="text-sm font-medium text-gray-700">Premium Track</span>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={handleSave} disabled={saving || !form.title}
              className="bg-pink-500 text-white font-bold px-6 py-3 rounded-xl disabled:opacity-50">
              {saving ? 'Saving...' : editing ? 'Update' : 'Add Track'}
            </button>
            <button onClick={resetForm} className="bg-gray-100 text-gray-700 font-medium px-6 py-3 rounded-xl">Cancel</button>
          </div>
        </div>
      )}

      {/* Track List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Track</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Preview</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tracks.map(track => {
                const isThisPlaying = previewTrackId === track.id;
                return (
                  <tr key={track.id} className={`hover:bg-gray-50 ${!track.is_active ? 'opacity-40' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{track.emoji}</span>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">{track.title}</p>
                          <p className="text-xs text-gray-400">{track.artist}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">{track.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      {track.is_premium ? (
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700">PRO</span>
                      ) : (
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700">Free</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {track.audio_url ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => togglePreview(track.audio_url, track.id)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                              isThisPlaying
                                ? 'bg-pink-500 text-white'
                                : 'bg-pink-50 text-pink-500 hover:bg-pink-100'
                            }`}
                          >
                            {isThisPlaying ? (
                              <div className="flex gap-0.5">
                                <div className="w-0.5 h-2.5 bg-white rounded-full animate-pulse" />
                                <div className="w-0.5 h-2.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                              </div>
                            ) : (
                              <SafeIcon name="Play" className="w-3.5 h-3.5 ml-0.5" />
                            )}
                          </button>
                          {isThisPlaying && (
                            <div className="w-16 bg-gray-200 rounded-full h-1 overflow-hidden">
                              <div className="bg-pink-500 h-full rounded-full transition-all duration-100" style={{ width: `${previewProgress}%` }} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No audio</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEdit(track)} className="text-blue-500 hover:text-blue-700 text-sm font-medium">Edit</button>
                        <button onClick={() => handleToggleActive(track)} className={`text-sm font-medium ${track.is_active ? 'text-amber-500' : 'text-green-500'}`}>
                          {track.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button onClick={() => handleDelete(track.id)} className="text-red-500 hover:text-red-700 text-sm font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MusicAdmin;